import { Asset } from 'components/common/types/Asset.types';
import { IContent, IJsonSheet } from 'json-as-xlsx';
import { formatNumber, formatPercent, FormatTypeEnum, formatValue } from 'modules/I18N';

import { CodeNameModel } from 'components/common/types';
import { RouteFrameCode } from 'components/common/types/Deal.types';
import { removeColumnsFields } from 'utils/removeColumnsFields';
import { formatExportDateAndTime } from './formatExportDateAndTime';

export type GenerateFrameListDataProps = {
  assets: Partial<Asset>[];
  currencyCode: string;
  localeCode: string;
  columns?: IJsonSheet['columns'];
  conditionalColumns?: Record<string, boolean>;
};

export const FRAME_LIST_LABELS: Record<string, string> = {
  mediaOwner: 'Media owner',
  frameId: 'Frame ID',
  vioohId: 'VIOOH ID',
  include: 'Include',
  latitude: 'Latitude',
  longitude: 'Longitude',
  width: 'Width',
  height: 'Height',
  motionType: 'Motion type',
  slotDuration: 'Slot duration',
  timezone: 'Timezone',
  visualUnitCode: 'Visual unit code',
  availability: 'Availability',
  street: 'Street',
  town: 'Town',
  postCode: 'PostCode',
  routeFrameCode: 'Route frame code',
  frameCode: 'Frame code',
  sot: 'SOT',
  impressions: 'Impressions',
  price: 'Price',
};

const allFrameListColumns: IJsonSheet['columns'] = [
  { label: FRAME_LIST_LABELS.mediaOwner, value: 'mediaOwner' },
  { label: FRAME_LIST_LABELS.frameId, value: 'frameId' },
  { label: FRAME_LIST_LABELS.vioohId, value: 'vioohId' },
  { label: FRAME_LIST_LABELS.latitude, value: 'latitude' },
  { label: FRAME_LIST_LABELS.longitude, value: 'longitude' },
  { label: FRAME_LIST_LABELS.width, value: 'width' },
  { label: FRAME_LIST_LABELS.height, value: 'height' },
  { label: FRAME_LIST_LABELS.motionType, value: 'motionType' },
  { label: FRAME_LIST_LABELS.slotDuration, value: 'slotDuration' },
  { label: FRAME_LIST_LABELS.timezone, value: 'timezone' },
  { label: FRAME_LIST_LABELS.visualUnitCode, value: 'visualUnitCode' },
  { label: FRAME_LIST_LABELS.availability, value: 'availability' },
  { label: FRAME_LIST_LABELS.street, value: 'street' },
  { label: FRAME_LIST_LABELS.town, value: 'town' },
  { label: FRAME_LIST_LABELS.postCode, value: 'postCode' },
  { label: FRAME_LIST_LABELS.routeFrameCode, value: 'routeFrameCode' },
  { label: FRAME_LIST_LABELS.frameCode, value: 'frameCode' },
  { label: FRAME_LIST_LABELS.sot, value: 'sot' },
  { label: FRAME_LIST_LABELS.impressions, value: 'impressions' },
  { label: FRAME_LIST_LABELS.price, value: 'price' },
];

export const generateFrameListData = ({
  assets,
  currencyCode,
  localeCode,
  columns = allFrameListColumns,
  conditionalColumns = {},
}: GenerateFrameListDataProps): IJsonSheet[] => {
  const columnsKeys = columns.map((column) => column.value);

  const content = assets.map(
    ({ siteType, sot: assetSot, impressions: assetImpressions, price, cpm, district, ...exportData }) => {
      const row: IContent = {};

      Object.entries(exportData).forEach(([key, value]) => {
        if (columnsKeys.includes(key)) {
          row[key] = value;
        }
      });

      if (columnsKeys.includes('sot'))
        row.sot = assetSot ? formatPercent({ value: assetSot / 100, localeCode }).replace(/\xA0/g, ' ') : '';

      if (columnsKeys.includes('impressions'))
        row.impressions = assetImpressions
          ? formatValue(assetImpressions, FormatTypeEnum.ROUNDED_NUMBER, currencyCode, localeCode)
          : '';

      if (columnsKeys.includes('price')) row.price = price ? formatNumber({ value: price as number, localeCode }) : '';

      if (columnsKeys.includes('town')) row.town = district?.town?.townName ?? '';

      return row;
    },
  );

  return [
    {
      sheet: 'Frame-List',
      columns: removeColumnsFields({
        columns,
        fields: conditionalColumns,
        key: 'value',
      }),
      content,
    },
  ];
};

export const getFrameListSettings = ({
  dealId,
  campaignName,
}: {
  dealId?: string;
  campaignName?: string;
} = {}): { fileName: string; extraLength: number; writeOptions: {} } => {
  const [date, time] = new Date().toISOString().split('T');
  const [hours, minutes] = time.split(':');
  const timestamp = `${date}_${hours}_${minutes}`;

  if (!dealId && !campaignName) {
    return {
      fileName: `frames-details_${formatExportDateAndTime(new Date())}`,
      extraLength: 3,
      writeOptions: {},
    };
  }

  const fileName = dealId ? `${dealId} – ${campaignName}_${timestamp}` : `${campaignName}_${timestamp}`;

  return {
    fileName,
    extraLength: 3,
    writeOptions: {},
  };
};

const updateIncludeExclude = (frameCodes: CodeNameModel[], value: CodeNameModel): CodeNameModel[] => {
  return frameCodes.map((frameCode) =>
    frameCode.code === value.code && frameCode.mediaOwner === value.mediaOwner
      ? { ...frameCode, include: value.include }
      : frameCode,
  );
};

export const findSelectedRouteFrameCode = (
  routeFrameCodes: RouteFrameCode[],
  code: string,
  mediaOwner?: string,
): RouteFrameCode | undefined =>
  routeFrameCodes.find((option) => {
    const isCodeMatch = option.code === code;

    if (option.mediaOwner && mediaOwner) {
      return isCodeMatch && option.mediaOwner === mediaOwner;
    }

    return isCodeMatch;
  });

const removeFrameByCode = (frames: CodeNameModel[], value: CodeNameModel): CodeNameModel[] => {
  return frames.filter(
    (frame) => frame.code !== value.code || (frame.code === value.code && frame.mediaOwner !== value.mediaOwner),
  );
};

export const updateFrameSelection = (
  frameCodes: RouteFrameCode[],
  value: CodeNameModel,
  isFrameRemovableOnClick: boolean = true,
): CodeNameModel[] => {
  const frameCodeExists = findSelectedRouteFrameCode(frameCodes, value.code, value.mediaOwner);

  if (frameCodeExists) {
    if (value.include !== undefined) {
      return updateIncludeExclude(frameCodes, value);
    }

    if (isFrameRemovableOnClick) {
      return removeFrameByCode(frameCodes, value);
    }
    return frameCodes;
  }

  return [...frameCodes, value];
};
